/**
 * AddCategoryModal Component
 *
 * Modal for creating category-level fee configurations.
 * Handles three entry points with a single form:
 * - Header "Add Fee Category": all fields editable
 * - Row "Add Market Segment": asset class pre-filled and locked
 * - Row "Add Sub-Asset Class": asset class + market segment pre-filled and locked
 *
 * Pre-fill is driven by `parentCategoryPath` prop length:
 * - [] → all editable
 * - ['futures'] → asset class locked
 * - ['futures', 'indices'] → asset class + market segment locked
 */

// 1. External dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// 2. Workspace packages
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  SelectItem,
  Input,
  Alert,
  useToastNotification,
} from '@enfomentis/ui';

// 3. Internal imports
import type { AssetClassType, MarketSegment, SubAssetClass } from '@/types/shared/AssetClass';
import type { AssetClass } from '@/types/shared/AssetClass';
import {
  ASSET_CLASS_TYPE_DISPLAY_MAP,
  MARKET_SEGMENT_DISPLAY_MAP,
  SUB_ASSET_CLASS_DISPLAY_MAP,
  VALID_MARKET_SEGMENTS_BY_ASSET_CLASS,
  VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS,
  VALID_SUB_ASSET_CLASSES_BY_ASSET_AND_SEGMENT,
} from '@/constants/assetClass';
import { useBrokers } from './context';

// =============================================================================
// Types
// =============================================================================

/**
 * Props for AddCategoryModal
 */
export interface AddCategoryModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal closes */
  onClose: () => void;
  /** Pre-filled category path from parent node (empty array for top-level) */
  parentCategoryPath: string[];
  /** Callback after successful add — passes new node ID for selection */
  onAdded: (nodeId: string) => void;
}

// =============================================================================
// Constants
// =============================================================================

/** Option key used for the "None" sub-asset class selection */
const NONE_KEY = '__none__';

// =============================================================================
// Component
// =============================================================================

export const AddCategoryModal: React.FC<AddCategoryModalProps> = ({
  isOpen,
  onClose,
  parentCategoryPath,
  onAdded,
}) => {
  // ---------------------------------------------------------------------------
  // Context & Hooks
  // ---------------------------------------------------------------------------

  const { addFeeConfiguration, selectedBroker } = useBrokers();
  const { showToast } = useToastNotification();

  // ---------------------------------------------------------------------------
  // Derived State (from props — stable across renders)
  // ---------------------------------------------------------------------------

  const isAssetClassLocked = parentCategoryPath.length >= 1;
  const isMarketSegmentLocked = parentCategoryPath.length >= 2;

  // Scope selectors to the level being added
  const showMarketSegment = parentCategoryPath.length <= 1;
  const showSubAssetClass =
    parentCategoryPath.length === 0 || parentCategoryPath.length === 2;
  const isSubAssetClassRequired = parentCategoryPath.length === 2;

  const modalTitle = useMemo(() => {
    if (isMarketSegmentLocked) return 'Add Sub-Asset Class';
    if (isAssetClassLocked) return 'Add Market Segment';
    return 'Add Fee Category';
  }, [isAssetClassLocked, isMarketSegmentLocked]);

  // ---------------------------------------------------------------------------
  // Form State
  // ---------------------------------------------------------------------------

  const [selectedAssetClass, setSelectedAssetClass] = useState<AssetClassType | null>(null);
  const [selectedMarketSegment, setSelectedMarketSegment] = useState<MarketSegment | null>(null);
  const [selectedSubAssetClass, setSelectedSubAssetClass] = useState<SubAssetClass | null>(null);
  const [customMarketSegment, setCustomMarketSegment] = useState('');
  const [customSubAssetClass, setCustomSubAssetClass] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // Reset & Pre-fill
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (isOpen) {
      // Pre-fill from parentCategoryPath
      setSelectedAssetClass(
        parentCategoryPath.length >= 1
          ? (parentCategoryPath[0] as AssetClassType)
          : null
      );
      setSelectedMarketSegment(
        parentCategoryPath.length >= 2
          ? (parentCategoryPath[1] as MarketSegment)
          : null
      );
      setSelectedSubAssetClass(null);
      setCustomMarketSegment('');
      setCustomSubAssetClass('');
      setIsSubmitting(false);
      setSubmitError(null);
    }
  }, [isOpen, parentCategoryPath]);

  // ---------------------------------------------------------------------------
  // Dynamic Options
  // ---------------------------------------------------------------------------

  const assetClassOptions = useMemo(
    () =>
      (Object.entries(ASSET_CLASS_TYPE_DISPLAY_MAP) as [AssetClassType, string][]).map(
        ([key, label]) => ({ key, label })
      ),
    []
  );

  const marketSegmentOptions = useMemo(() => {
    if (!selectedAssetClass) return [];
    const validSegments = VALID_MARKET_SEGMENTS_BY_ASSET_CLASS[selectedAssetClass];
    return validSegments.map((key) => ({
      key,
      label: MARKET_SEGMENT_DISPLAY_MAP[key] ?? key,
    }));
  }, [selectedAssetClass]);

  const subAssetClassOptions = useMemo(() => {
    if (!selectedAssetClass) return [];
    // Use per-segment filtering when a market segment is selected
    if (selectedMarketSegment) {
      const segmentMap = VALID_SUB_ASSET_CLASSES_BY_ASSET_AND_SEGMENT[selectedAssetClass];
      const segmentSubs = (segmentMap as Record<string, readonly SubAssetClass[] | undefined>)[selectedMarketSegment];
      if (segmentSubs) {
        return segmentSubs.map((key) => ({
          key,
          label: SUB_ASSET_CLASS_DISPLAY_MAP[key] ?? key,
        }));
      }
    }
    // Fall back to asset-class-level filtering (no segment selected yet)
    const validSubs = VALID_SUB_ASSET_CLASSES_BY_ASSET_CLASS[selectedAssetClass];
    return validSubs.map((key) => ({
      key,
      label: SUB_ASSET_CLASS_DISPLAY_MAP[key] ?? key,
    }));
  }, [selectedAssetClass, selectedMarketSegment]);

  // ---------------------------------------------------------------------------
  // Filter Existing Options
  // ---------------------------------------------------------------------------

  const existingMarketSegments = useMemo(() => {
    if (parentCategoryPath.length !== 1 || !selectedBroker) return new Set<string>();
    const assetClass = parentCategoryPath[0];
    const existing = new Set<string>();
    for (const config of selectedBroker.feeConfigurations) {
      const cat = config.category;
      if (
        config.status !== 'archived' &&
        !config.instrumentId &&
        cat &&
        cat.assetClass === assetClass
      ) {
        existing.add(cat.marketSegment);
      }
    }
    return existing;
  }, [parentCategoryPath, selectedBroker]);

  const existingSubAssetClasses = useMemo(() => {
    if (parentCategoryPath.length !== 2 || !selectedBroker) return new Set<string>();
    const [assetClass, marketSegment] = parentCategoryPath;
    const existing = new Set<string>();
    for (const config of selectedBroker.feeConfigurations) {
      const cat = config.category;
      if (
        config.status !== 'archived' &&
        !config.instrumentId &&
        cat &&
        cat.assetClass === assetClass &&
        cat.marketSegment === marketSegment &&
        cat.subAssetClass
      ) {
        existing.add(cat.subAssetClass);
      }
    }
    return existing;
  }, [parentCategoryPath, selectedBroker]);

  const filteredMarketSegmentOptions = useMemo(
    () => marketSegmentOptions.filter((opt) => !existingMarketSegments.has(opt.key)),
    [marketSegmentOptions, existingMarketSegments]
  );

  const filteredSubAssetClassOptions = useMemo(
    () => subAssetClassOptions.filter((opt) => !existingSubAssetClasses.has(opt.key)),
    [subAssetClassOptions, existingSubAssetClasses]
  );

  // Detect when all options at a required level are exhausted
  const isMarketSegmentExhausted =
    showMarketSegment && !isMarketSegmentLocked &&
    selectedAssetClass !== null && filteredMarketSegmentOptions.length === 0;

  const isSubAssetClassExhausted =
    isSubAssetClassRequired &&
    selectedAssetClass !== null && filteredSubAssetClassOptions.length === 0;

  // Reset stale selections when filtered options change
  useEffect(() => {
    if (
      selectedMarketSegment &&
      !isMarketSegmentLocked &&
      filteredMarketSegmentOptions.length > 0 &&
      !filteredMarketSegmentOptions.some((opt) => opt.key === selectedMarketSegment)
    ) {
      setSelectedMarketSegment(null);
      setCustomMarketSegment('');
    }
  }, [selectedMarketSegment, isMarketSegmentLocked, filteredMarketSegmentOptions]);

  useEffect(() => {
    if (
      selectedSubAssetClass &&
      filteredSubAssetClassOptions.length > 0 &&
      !filteredSubAssetClassOptions.some((opt) => opt.key === selectedSubAssetClass)
    ) {
      setSelectedSubAssetClass(null);
      setCustomSubAssetClass('');
    }
  }, [selectedSubAssetClass, filteredSubAssetClassOptions]);

  // ---------------------------------------------------------------------------
  // Cascade Resets
  // ---------------------------------------------------------------------------

  const handleAssetClassChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const value = Array.from(keys)[0] as AssetClassType | undefined;
      setSelectedAssetClass(value ?? null);

      // Reset dependent fields when asset class changes
      if (!isMarketSegmentLocked) {
        setSelectedMarketSegment(null);
        setCustomMarketSegment('');
      }
      setSelectedSubAssetClass(null);
      setCustomSubAssetClass('');
      setSubmitError(null);
    },
    [isMarketSegmentLocked]
  );

  const handleMarketSegmentChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const value = Array.from(keys)[0] as MarketSegment | undefined;
      setSelectedMarketSegment(value ?? null);
      setCustomMarketSegment('');
      // Cascade reset: sub-asset class options change per segment
      setSelectedSubAssetClass(null);
      setCustomSubAssetClass('');
      setSubmitError(null);
    },
    []
  );

  const handleSubAssetClassChange = useCallback(
    (keys: 'all' | Set<React.Key>): void => {
      if (keys === 'all') return;
      const raw = Array.from(keys)[0] as string | undefined;
      // Treat NONE_KEY as "no sub-asset class"
      setSelectedSubAssetClass(raw && raw !== NONE_KEY ? (raw as SubAssetClass) : null);
      setCustomSubAssetClass('');
      setSubmitError(null);
    },
    []
  );

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const isFormValid = useMemo(() => {
    // Exhausted options prevent valid submission
    if (isMarketSegmentExhausted || isSubAssetClassExhausted) return false;

    // Asset class always required
    if (!selectedAssetClass) return false;

    // Market segment required when shown (length 0 or 1)
    if (showMarketSegment) {
      if (!selectedMarketSegment) return false;
      if (selectedMarketSegment === 'other' && !customMarketSegment.trim()) return false;
    }

    // Sub-asset class required when adding at that level (length 2)
    if (isSubAssetClassRequired) {
      if (!selectedSubAssetClass) return false;
      if (selectedSubAssetClass === 'other' && !customSubAssetClass.trim()) return false;
    }

    // Optional sub-asset class custom text validation (header form)
    if (showSubAssetClass && !isSubAssetClassRequired) {
      if (selectedSubAssetClass === 'other' && !customSubAssetClass.trim()) return false;
    }

    return true;
  }, [
    selectedAssetClass,
    selectedMarketSegment,
    customMarketSegment,
    selectedSubAssetClass,
    customSubAssetClass,
    showMarketSegment,
    showSubAssetClass,
    isSubAssetClassRequired,
    isMarketSegmentExhausted,
    isSubAssetClassExhausted,
  ]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = useCallback(async (): Promise<void> => {
    if (!selectedAssetClass || !selectedMarketSegment) return;

    setSubmitError(null);
    setIsSubmitting(true);

    try {
      const category: AssetClass = {
        assetClass: selectedAssetClass,
        marketSegment: selectedMarketSegment,
        ...(selectedMarketSegment === 'other' &&
          customMarketSegment.trim() && {
            customMarketSegment: customMarketSegment.trim(),
          }),
        ...(selectedSubAssetClass && { subAssetClass: selectedSubAssetClass }),
        ...(selectedSubAssetClass === 'other' &&
          customSubAssetClass.trim() && {
            customSubAssetClass: customSubAssetClass.trim(),
          }),
      };

      const result = await addFeeConfiguration({ category });

      if (result.success) {
        showToast({
          title: 'Fee configuration added',
          color: 'success',
        });
        onAdded(result.nodeId);
        onClose();
      } else {
        // Exhaustive handling of all failure reasons
        switch (result.reason) {
          case 'active_exists':
            setSubmitError(
              'A fee configuration already exists at this category path.'
            );
            break;
          case 'invalid_path':
            setSubmitError(
              'Invalid category path. Please select at least an asset class and market segment.'
            );
            break;
          case 'no_broker':
            setSubmitError('No broker selected.');
            break;
          case 'unknown_error':
            setSubmitError('An unexpected error occurred. Please try again.');
            break;
          default: {
            const _exhaustive: never = result.reason;
            return _exhaustive;
          }
        }
      }
    } catch (error) {
      console.error('Failed to add fee configuration:', error);
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedAssetClass,
    selectedMarketSegment,
    customMarketSegment,
    selectedSubAssetClass,
    customSubAssetClass,
    addFeeConfiguration,
    showToast,
    onAdded,
    onClose,
  ]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => !open && onClose()}
      size="md"
      placement="center"
      isDismissable={!isSubmitting}
      hideCloseButton={isSubmitting}
    >
      <ModalContent>
        <ModalHeader>{modalTitle}</ModalHeader>

        <ModalBody className="gap-4">
          {/* Asset Class */}
          <Select
            label="Asset Class"
            placeholder="Select asset class"
            selectedKeys={selectedAssetClass ? [selectedAssetClass] : []}
            onSelectionChange={handleAssetClassChange}
            isDisabled={isAssetClassLocked}
            isRequired
          >
            {assetClassOptions.map((opt) => (
              <SelectItem key={opt.key}>{opt.label}</SelectItem>
            ))}
          </Select>

          {/* Market Segment (shown for header "Add Fee Category" and row "Add Market Segment") */}
          {showMarketSegment && (
            <>
              <Select
                label="Market Segment"
                placeholder="Select market segment"
                selectedKeys={selectedMarketSegment ? [selectedMarketSegment] : []}
                onSelectionChange={handleMarketSegmentChange}
                isDisabled={isMarketSegmentLocked || !selectedAssetClass}
                isRequired
              >
                {filteredMarketSegmentOptions.map((opt) => (
                  <SelectItem key={opt.key}>{opt.label}</SelectItem>
                ))}
              </Select>

              {/* Custom Market Segment (when "Other" selected) */}
              {selectedMarketSegment === 'other' && (
                <Input
                  label="Custom Market Segment"
                  placeholder="Enter custom market segment name"
                  value={customMarketSegment}
                  onValueChange={(value) => {
                    setCustomMarketSegment(value);
                    setSubmitError(null);
                  }}
                  isRequired
                />
              )}
            </>
          )}

          {/* Sub-Asset Class (shown for header "Add Fee Category" and row "Add Sub-Asset Class") */}
          {showSubAssetClass && (
            <>
              <Select
                label="Sub-Asset Class"
                placeholder={isSubAssetClassRequired ? 'Select sub-asset class' : 'Select sub-asset class (optional)'}
                selectedKeys={
                  selectedSubAssetClass
                    ? [selectedSubAssetClass]
                    : isSubAssetClassRequired
                      ? []
                      : [NONE_KEY]
                }
                onSelectionChange={handleSubAssetClassChange}
                isDisabled={!selectedAssetClass}
                isRequired={isSubAssetClassRequired}
              >
                {isSubAssetClassRequired
                  ? filteredSubAssetClassOptions.map((opt) => (
                      <SelectItem key={opt.key}>{opt.label}</SelectItem>
                    ))
                  : [
                      <SelectItem key={NONE_KEY}>None</SelectItem>,
                      ...filteredSubAssetClassOptions.map((opt) => (
                        <SelectItem key={opt.key}>{opt.label}</SelectItem>
                      )),
                    ]}
              </Select>

              {/* Custom Sub-Asset Class (when "Other" selected) */}
              {selectedSubAssetClass === 'other' && (
                <Input
                  label="Custom Sub-Asset Class"
                  placeholder="Enter custom sub-asset class name"
                  value={customSubAssetClass}
                  onValueChange={(value) => {
                    setCustomSubAssetClass(value);
                    setSubmitError(null);
                  }}
                  isRequired
                />
              )}
            </>
          )}

          {/* Exhausted options helper text */}
          {isMarketSegmentExhausted && (
            <Alert color="warning" variant="flat">
              All market segments are already configured for this asset class.
            </Alert>
          )}
          {isSubAssetClassExhausted && (
            <Alert color="warning" variant="flat">
              All sub-asset classes are already configured for this market segment.
            </Alert>
          )}

          {/* Error Alert */}
          {submitError && (
            <Alert color="danger" variant="flat">
              {submitError}
            </Alert>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="flat" onPress={onClose} isDisabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isSubmitting}
            isDisabled={!isFormValid}
          >
            Create
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AddCategoryModal;
